import type { DiagramSemanticProfile, DiagramType } from '../types/diagramSpec';
import type { PhotoSearchMatchViewModel } from '../types/photoSearch';

export type PhotoSearchDiagramContext = {
  imageRef: string;
  selectedCropImageRef: string | null;
  diagramType: DiagramType;
  semanticProfile: DiagramSemanticProfile;
  selectedProblemId: string;
  selectedSegmentId: string | null;
  questionNumber: string | null;
};

const diagramTypes: ReadonlySet<string> = new Set([
  'function',
  'conic',
  'triangle',
  'circle',
]);

const semanticProfiles: ReadonlySet<string> = new Set([
  'default',
  'circle_diameter_extended_tangents',
]);

export const parseDiagramType = (value: unknown): DiagramType | null =>
  typeof value === 'string' && diagramTypes.has(value) ? value as DiagramType : null;

export const parseDiagramSemanticProfile = (
  value: unknown
): DiagramSemanticProfile | null =>
  typeof value === 'string' && semanticProfiles.has(value)
    ? value as DiagramSemanticProfile
    : null;

const getDiagramText = (match: PhotoSearchMatchViewModel): string =>
  [
    match.title,
    match.description,
    match.ocrText,
    match.normalizedProblem,
    ...match.knowledgePoints,
  ]
    .filter(Boolean)
    .join(' ');

const classifyDiagram = (
  match: PhotoSearchMatchViewModel
): Pick<PhotoSearchDiagramContext, 'diagramType' | 'semanticProfile'> | null => {
  const text = getDiagramText(match);
  const isCircle = /圆|circle/i.test(text);
  const hasDiameter = /直径|diameter/i.test(text);
  const hasExtendedLine = /延长线|延长|extended\s+line/i.test(text);
  const hasPairedTangentLabels =
    /[A-Z]{2}\s*[、,，和与]\s*[A-Z]{2}\s*(?:均|分别)?\s*(?:为|是|作)?\s*切线/i.test(text);
  const hasTwoTangents =
    /两条切线|二条切线|两切线|two\s+tangents/i.test(text) ||
    hasPairedTangentLabels;

  if (isCircle && hasDiameter && hasExtendedLine && hasTwoTangents) {
    return {
      diagramType: 'circle',
      semanticProfile: 'circle_diameter_extended_tangents',
    };
  }
  if (isCircle) {
    return { diagramType: 'circle', semanticProfile: 'default' };
  }
  if (/椭圆|圆锥曲线|ellipse|conic/i.test(text)) {
    return { diagramType: 'conic', semanticProfile: 'default' };
  }
  if (/三角形|triangle/i.test(text)) {
    return { diagramType: 'triangle', semanticProfile: 'default' };
  }
  if (/函数|抛物线|function|parabola/i.test(text)) {
    return { diagramType: 'function', semanticProfile: 'default' };
  }
  return null;
};

export const resolvePhotoSearchDiagramContext = (
  imageRef: string,
  selectedMatch: PhotoSearchMatchViewModel | null,
  detectedQuestionCount = 1
): PhotoSearchDiagramContext | null => {
  if (!imageRef.trim() || !selectedMatch) {
    return null;
  }

  const classified = classifyDiagram(selectedMatch);
  if (!classified) {
    return null;
  }

  const cropImageRef = selectedMatch.cropImageRef?.trim() || null;
  if (detectedQuestionCount > 1 && !cropImageRef) {
    return null;
  }

  return {
    imageRef,
    selectedCropImageRef: cropImageRef,
    ...classified,
    selectedProblemId: selectedMatch.id,
    selectedSegmentId: selectedMatch.segmentId,
    questionNumber: selectedMatch.questionNumber,
  };
};
